/**
 * aw-toggle - Toggle Switch Component
 *
 * A toggle switch with smooth animation and accessible keyboard support.
 *
 * Attributes:
 *   checked: boolean - whether the toggle is on
 *   disabled: boolean - prevents interaction
 *   name: string - form field name
 *   value: string - value when checked (default: "on")
 *
 * Events:
 *   aw-change: Fired when toggle state changes
 *     detail: { checked: boolean, name: string, value: string }
 *
 * Slots:
 *   default: Label text
 *   help: Help icon or tooltip content
 *
 * CSS Custom Properties:
 *   --toggle-width: Width of toggle track (default: 44px)
 *   --toggle-height: Height of toggle track (default: 24px)
 *   --toggle-bg: Background when off (default: var(--bg-elevated))
 *   --toggle-bg-checked: Background when on (default: rgba(52, 211, 153, 0.15))
 *   --toggle-knob: Knob color when off (default: var(--text-tertiary))
 *   --toggle-knob-checked: Knob color when on (default: #34d399)
 *
 * Usage:
 *   <aw-toggle>Enable feature</aw-toggle>
 *   <aw-toggle checked name="passkeys">Passkeys</aw-toggle>
 *   <aw-toggle disabled>Unavailable</aw-toggle>
 */

import { AWElement } from './base';

export class AWToggle extends AWElement {
  static observedAttributes = ['checked', 'disabled', 'name', 'value'];

  private _internals: ElementInternals | null = null;

  constructor() {
    super();
    if ('attachInternals' in this) {
      this._internals = this.attachInternals();
    }
  }

  static get formAssociated(): boolean {
    return true;
  }

  protected static get styles(): string {
    return `
      /* ============================================================
         Host
         ============================================================ */
      :host {
        display: inline-flex;
        align-items: center;
        gap: var(--space-3);
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        user-select: none;
      }

      :host([disabled]) {
        cursor: not-allowed;
        opacity: 0.5;
        pointer-events: none;
      }

      /* ============================================================
         Track (pill-shaped background)
         ============================================================ */
      .track {
        position: relative;
        width: var(--toggle-width, 44px);
        height: var(--toggle-height, 24px);
        background: var(--toggle-bg, var(--bg-elevated));
        border-radius: calc(var(--toggle-height, 24px) / 2);
        flex-shrink: 0;
      }

      /* Checked background via pseudo-element (GPU-accelerated opacity) */
      .track::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: var(--toggle-bg-checked, rgba(52, 211, 153, 0.15));
        opacity: 0;
        will-change: opacity;
        transition: opacity 400ms ease-out;
      }

      :host([checked]) .track::before {
        opacity: 1;
        transition-duration: 200ms;
      }

      /* ============================================================
         Knob (sliding circle)
         Animation: fast on (200ms overshoot), slow off (400ms smooth)
         ============================================================ */
      .knob {
        position: absolute;
        top: 3px;
        left: 3px;
        width: calc(var(--toggle-height, 24px) - 6px);
        height: calc(var(--toggle-height, 24px) - 6px);
        border-radius: 50%;
        will-change: transform;
        transform: translateX(0) scale(1);
        transition: transform 400ms cubic-bezier(0.3, 0.7, 0.4, 1);
      }

      :host([checked]) .knob {
        transform: translateX(calc(var(--toggle-width, 44px) - var(--toggle-height, 24px))) scale(1);
        transition: transform 200ms cubic-bezier(0.3, 0.7, 0.4, 1.5);
      }

      /* Knob color layers (GPU-accelerated opacity + filter) */
      .knob::before,
      .knob::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        will-change: opacity, filter;
        transition: opacity 400ms ease-out, filter 150ms ease-out;
      }

      .knob::before {
        background: var(--toggle-knob, var(--text-tertiary));
      }

      .knob::after {
        background: var(--toggle-knob-checked, #34d399);
        opacity: 0;
      }

      :host([checked]) .knob::before {
        opacity: 0;
        transition-duration: 200ms;
      }

      :host([checked]) .knob::after {
        opacity: 1;
        transition-duration: 200ms;
      }

      /* ============================================================
         Interaction States
         ============================================================ */

      /* Hover: subtle glow */
      :host(:hover) .knob::before,
      :host(:hover) .knob::after {
        filter: brightness(1.1);
      }

      /* Active: squish without breaking transition flow */
      :host(:active) .knob {
        transform: translateX(0) scale(0.9);
      }

      :host([checked]:active) .knob {
        transform: translateX(calc(var(--toggle-width, 44px) - var(--toggle-height, 24px))) scale(0.9);
      }

      /* ============================================================
         Form & Accessibility
         ============================================================ */
      input {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      input:focus-visible + .track {
        outline: 2px solid var(--accent-primary);
        outline-offset: 2px;
      }

      /* ============================================================
         Label & Help
         ============================================================ */
      .label {
        font-size: var(--font-size-base);
        font-weight: 500;
        color: var(--text-primary);
        line-height: 1;
      }

      .help {
        display: flex;
        align-items: center;
      }

      ::slotted([slot="help"]) {
        width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-elevated);
        border-radius: 50%;
        font-size: 10px;
        font-weight: 600;
        color: var(--text-tertiary);
        cursor: help;
      }

      /* ============================================================
         Reduced Motion
         ============================================================ */
      @media (prefers-reduced-motion: reduce) {
        .track::before,
        .knob,
        .knob::before,
        .knob::after {
          transition: none;
        }
      }
    `;
  }

  protected render(): string {
    const name = this.getAttribute('name') || '';
    const value = this.getAttribute('value') || 'on';
    const checked = this.hasAttribute('checked');
    const disabled = this.hasAttribute('disabled');

    return `
      <input
        type="checkbox"
        id="toggle"
        name="${name}"
        value="${value}"
        ${checked ? 'checked' : ''}
        ${disabled ? 'disabled' : ''}
        part="input"
      />
      <div class="track" part="track">
        <div class="knob" part="knob"></div>
      </div>
      <span class="label" part="label">
        <slot></slot>
      </span>
      <span class="help">
        <slot name="help"></slot>
      </span>
    `;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.setupEventListeners();
    this.updateFormValue();

    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'switch');
    }
    this.setAttribute('aria-checked', String(this.checked));
  }

  private setupEventListeners(): void {
    // Click on host toggles state
    this.addEventListener('click', this.handleClick);

    // Keyboard support
    this.addEventListener('keydown', this.handleKeydown);

    // Make focusable
    if (!this.hasAttribute('tabindex') && !this.hasAttribute('disabled')) {
      this.setAttribute('tabindex', '0');
    }
  }

  private handleClick = (e: Event): void => {
    if (this.hasAttribute('disabled')) {
      e.preventDefault();
      return;
    }

    this.toggle();
  };

  private handleKeydown = (e: KeyboardEvent): void => {
    if (this.hasAttribute('disabled')) return;

    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this.toggle();
    }
  };

  /**
   * Toggle the checked state
   */
  toggle(): void {
    this.checked = !this.checked;
  }

  /**
   * Get/set checked state
   */
  get checked(): boolean {
    return this.hasAttribute('checked');
  }

  set checked(value: boolean) {
    const wasChecked = this.checked;
    this.setBoolAttr('checked', value);
    this.setAttribute('aria-checked', String(value));
    this.updateFormValue();

    // Emit change event only if state actually changed
    if (wasChecked !== value) {
      this.emit('aw-change', {
        checked: value,
        name: this.getAttribute('name') || '',
        value: this.getAttribute('value') || 'on'
      });
    }
  }

  /**
   * Get/set disabled state
   */
  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(value: boolean) {
    this.setBoolAttr('disabled', value);
    if (value) {
      this.removeAttribute('tabindex');
    } else {
      this.setAttribute('tabindex', '0');
    }
  }

  /**
   * Update form value for form association
   */
  private updateFormValue(): void {
    if (this._internals) {
      const value = this.checked ? (this.getAttribute('value') || 'on') : null;
      this._internals.setFormValue(value);
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    // Don't call super for 'checked' - we handle it without re-rendering
    // Re-rendering would destroy elements and prevent CSS transitions
    if (name === 'checked') {
      this.setAttribute('aria-checked', String(this.hasAttribute('checked')));
      this.updateFormValue();
      // Sync the hidden input without re-rendering
      const input = this.shadow.querySelector('input');
      if (input) {
        input.checked = this.hasAttribute('checked');
      }
    } else {
      super.attributeChangedCallback(name, oldValue, newValue);
    }
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
  }
}

export default AWToggle;
